import { useParams, Link, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Clock, Calendar, MapPin, ClipboardList, Phone, BookOpen, Plane, Sparkles } from "lucide-react";
import { useSEO } from "../lib/useSEO";
import { useJsonLd, buildArticleSchema, buildBreadcrumbSchema } from "../lib/useJsonLd";
import { ARTICLES_BY_SLUG, articlesForNeighborhood } from "../lib/blogConfig";
import { NEIGHBORHOODS } from "../lib/neighborhoodConfig";
import { TYPEFORM_QUIZ_URL } from "../lib/externalLinks";
import { BRAND } from "../lib/brandConfig";
import LeadCaptureForm from "../components/LeadCaptureForm";
import StickyLeadModal from "../components/StickyLeadModal";

/**
 * /blog/:slug — Single article renderer for both cornerstone and supporting types.
 * Cornerstone articles have a fixed 8-section structure (per content spec);
 * supporting articles render dynamically from their template's sections().
 */
const BlogArticle = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const article = ARTICLES_BY_SLUG[slug];

  useEffect(() => {
    if (!article) navigate("/blog", { replace: true });
  }, [article, navigate]);

  useSEO({
    title: article?.title || "Urban Nest Quest Blog",
    description: article?.metaDescription || "",
    keywords: article?.keywords || [],
    path: `/blog/${slug || ""}`,
  });

  useJsonLd(
    `${slug || "article"}-article`,
    article
      ? buildArticleSchema({
          title: article.title,
          description: article.metaDescription,
          slug,
          publishedAt: article.publishedAt,
          keywords: article.keywords,
        })
      : null
  );
  useJsonLd(
    `${slug || "article"}-breadcrumb`,
    article
      ? buildBreadcrumbSchema([
          { name: "Home", url: "https://urbannestquest.com/" },
          { name: "Blog", url: "https://urbannestquest.com/blog" },
          { name: article.title, url: `https://urbannestquest.com/blog/${slug}` },
        ])
      : null
  );

  if (!article) return null;

  const n = NEIGHBORHOODS[article.neighborhood];
  // Related articles in this neighborhood, excluding the current one
  const related = articlesForNeighborhood(article.neighborhood)
    .filter((a) => a.slug !== slug)
    .slice(0, 4);

  return (
    <div className="bg-[#FAFAF7] pt-20" data-testid={`blog-article-${slug}`}>
      {/* Breadcrumb */}
      <nav className="container-main pt-6 text-xs text-[#333333]/70" aria-label="Breadcrumb">
        <ol className="flex flex-wrap items-center gap-1.5">
          <li><Link to="/" className="hover:text-[#C8A96B]">Home</Link></li>
          <li>›</li>
          <li><Link to="/blog" className="hover:text-[#C8A96B]">Blog</Link></li>
          <li>›</li>
          <li className="text-[#1F2A44] font-semibold truncate max-w-[220px] sm:max-w-none">{article.title}</li>
        </ol>
      </nav>

      {/* Article header */}
      <article className="container-main py-10 sm:py-14">
        <div className="max-w-3xl mx-auto">
          <motion.header initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
            <p className="text-[11px] uppercase tracking-widest text-[#C8A96B] font-bold">
              {article.type === "cornerstone" ? "Neighborhood Guide" : "Renter Resource"}
              {n && ` · ${n.name}`}
            </p>
            <h1 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1F2A44] font-['Playfair_Display'] leading-tight"
                data-testid="blog-article-h1">
              {article.h1}
            </h1>
            <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-[#333333]/60">
              <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {article.publishedAt}</span>
              <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {article.readingMinutes} min read</span>
              <span className="inline-flex items-center gap-1"><BookOpen className="w-3.5 h-3.5" /> Urban Nest Quest</span>
            </div>
          </motion.header>

          {/* Body */}
          <div className="mt-8 prose prose-lg max-w-none">
            {article.type === "cornerstone"
              ? <CornerstoneBody article={article} />
              : <SupportingBody article={article} />}
          </div>

          {/* Inline CTA after body */}
          <div className="mt-10 p-6 sm:p-8 rounded-2xl bg-[#1F2A44] text-white text-center">
            <h2 className="text-2xl font-bold font-['Playfair_Display']">
              Ready to find your place in {n?.name || "Houston"}?
            </h2>
            <p className="mt-2 text-white/75 text-sm max-w-lg mx-auto">
              Free apartment locating. Properties pay us — you pay nothing.
            </p>
            <div className="mt-5 flex flex-wrap justify-center gap-3">
              <Link to={n ? `/apartments/${n.slug}` : "/apartment-search"}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#C8A96B] hover:bg-[#B5915A] text-white font-bold transition-colors"
                    data-testid="article-cta-search">
                <MapPin className="w-4 h-4" /> Browse {n?.name || "Houston"} listings
              </Link>
              <a href={TYPEFORM_QUIZ_URL} target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border border-white/30 hover:bg-white hover:text-[#1F2A44] font-semibold transition-colors"
                 data-testid="article-cta-quiz">
                <ClipboardList className="w-4 h-4" /> Take the Match Quiz
              </a>
              <a href={BRAND.smsHref}
                 className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border border-white/30 hover:bg-white hover:text-[#1F2A44] font-semibold transition-colors"
                 data-testid="article-cta-text">
                <Phone className="w-4 h-4" /> Text us
              </a>
            </div>
          </div>
        </div>
      </article>

      {/* Lead form */}
      <section className="bg-white border-y border-[#D9D9D9]">
        <div className="container-main py-12 sm:py-16">
          <div className="grid lg:grid-cols-2 gap-8 items-center max-w-5xl mx-auto">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-[#1F2A44] font-['Playfair_Display']">
                Get matched in {n?.name || "Houston"}
              </h2>
              <p className="mt-3 text-sm text-[#333333]/80 max-w-md">
                Submit your details — your locator will text the best matches in {n?.name || "Houston"} within 30 minutes (avg.).
              </p>
            </div>
            <LeadCaptureForm
              source={article.leadSource}
              submitLabel={`View ${n?.name || "Houston"} Apartments`}
            />
          </div>
        </div>
      </section>

      {/* Related articles */}
      {related.length > 0 && (
        <section className="container-main py-12 sm:py-16">
          <div className="flex items-end justify-between gap-4 flex-wrap mb-6">
            <div>
              <p className="text-[11px] uppercase tracking-widest text-[#C8A96B] font-bold">Keep reading</p>
              <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-[#1F2A44] font-['Playfair_Display']">
                More on {n?.name}
              </h2>
            </div>
            <Link to="/blog" className="inline-flex items-center gap-2 text-sm font-semibold text-[#1F2A44] hover:text-[#C8A96B]">
              Browse all articles <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {related.map((r) => (
              <Link key={r.slug} to={`/blog/${r.slug}`}
                    className="group p-5 rounded-xl border border-[#D9D9D9] bg-white hover:border-[#C8A96B] hover:shadow-md transition-all"
                    data-testid={`article-related-${r.slug}`}>
                <p className="text-[10px] uppercase tracking-widest text-[#C8A96B] font-bold">
                  {r.type === "cornerstone" ? "Guide" : "Resource"}
                </p>
                <h3 className="mt-1.5 text-base font-bold text-[#1F2A44] font-['Playfair_Display'] group-hover:text-[#C8A96B] transition-colors leading-snug">
                  {r.title}
                </h3>
                <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[#C8A96B]">
                  Read article <ArrowRight className="w-3 h-3" />
                </span>
              </Link>
            ))}
          </div>

          {/* Service cross-links */}
          <div className="mt-10 grid sm:grid-cols-3 gap-3 text-sm">
            <Link to="/relocation-services" className="flex items-center gap-3 p-4 rounded-xl bg-white border border-[#D9D9D9] hover:border-[#C8A96B] transition-colors"
                  data-testid="article-link-relocation">
              <Plane className="w-5 h-5 text-[#C8A96B]" />
              <span className="font-semibold text-[#1F2A44]">Relocation Services</span>
            </Link>
            <a href={TYPEFORM_QUIZ_URL} target="_blank" rel="noopener noreferrer"
               className="flex items-center gap-3 p-4 rounded-xl bg-white border border-[#D9D9D9] hover:border-[#C8A96B] transition-colors"
               data-testid="article-link-quiz">
              <ClipboardList className="w-5 h-5 text-[#C8A96B]" />
              <span className="font-semibold text-[#1F2A44]">Take the Apartment Match Quiz</span>
            </a>
            <Link to="/luxury-apartments" className="flex items-center gap-3 p-4 rounded-xl bg-white border border-[#D9D9D9] hover:border-[#C8A96B] transition-colors"
                  data-testid="article-link-luxury">
              <Sparkles className="w-5 h-5 text-[#C8A96B]" />
              <span className="font-semibold text-[#1F2A44]">Luxury Houston Apartments</span>
            </Link>
          </div>
        </section>
      )}

      <StickyLeadModal source={`${article.leadSource}_sticky`} storageKey={`unq_sticky_blog_${slug}_v1`} />
    </div>
  );
};

/* ---------- Body renderers ---------- */

const Section = ({ h, p }) => (
  <section className="mb-7">
    <h2 className="text-xl sm:text-2xl font-bold text-[#1F2A44] font-['Playfair_Display'] mt-8 mb-3">{h}</h2>
    <p className="text-[15px] sm:text-base text-[#333333]/85 leading-relaxed">{p}</p>
  </section>
);

const CornerstoneBody = ({ article }) => {
  const b = article.body;
  return (
    <>
      <p className="text-[17px] sm:text-lg text-[#333333]/90 leading-relaxed font-medium border-l-4 border-[#C8A96B] pl-4">
        {b.overview}
      </p>

      <Section h="Major employers"
               p={Array.isArray(b.employers) ? `${b.employers.join(", ")}.` : b.employers} />
      <Section h="Schools" p={b.schools} />
      <Section h="Dining and entertainment" p={b.dining} />
      <Section h="Commute information" p={b.commute} />
      <Section h="Apartment price expectations" p={b.pricing} />
      <Section h="Who this neighborhood is best for" p={b.bestFor} />
    </>
  );
};

const SupportingBody = ({ article }) => (
  <>
    <p className="text-[17px] sm:text-lg text-[#333333]/90 leading-relaxed font-medium border-l-4 border-[#C8A96B] pl-4">
      {article.intro}
    </p>
    {(article.sections || []).map((s) => <Section key={s.h} h={s.h} p={s.p} />)}
  </>
);

export default BlogArticle;
